import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"

interface UnsavedChangesDialogProps {
  isOpen: boolean
  /** A draft save is in flight. */
  isSaving: boolean
  /** Whether the current form is valid enough to save (assets + primary text). */
  canSave: boolean
  /** Save the draft, then continue to where the user was headed. */
  onSave: () => void
  /** Discard changes and continue navigating. */
  onLeave: () => void
  /** Cancel the navigation and stay on the editor. */
  onStay: () => void
}

/**
 * Shown when the user tries to leave the AdForge editor with unsaved changes.
 * Offers to save the work as a draft before leaving, leave anyway, or stay.
 */
export default function UnsavedChangesDialog({
  isOpen,
  isSaving,
  canSave,
  onSave,
  onLeave,
  onStay,
}: UnsavedChangesDialogProps) {
  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/85 backdrop-blur-sm overflow-y-auto py-10"
      onClick={onStay}
    >
      <div
        className="relative w-full max-w-md mx-4 bg-system-gray-900 rounded-xl border border-system-gray-800 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-6 pt-6">
          <ExclamationTriangleIcon className="w-6 h-6 text-warning flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-white">
              Save your changes?
            </h2>
            <p className="text-sm text-system-gray-400 mt-1">
              You have unsaved changes to this creative. Save it as a draft so
              you can pick up where you left off, or leave without saving.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2 px-6 py-6">
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving || !canSave}
            title={
              canSave
                ? undefined
                : "Add at least one asset and primary text to save a draft"
            }
            className="btn btn-primary btn-sm gap-2 disabled:opacity-50"
          >
            {isSaving && (
              <span className="loading loading-spinner loading-xs" />
            )}
            {isSaving ? "Saving draft…" : "Save as draft & leave"}
          </button>
          <button
            type="button"
            onClick={onLeave}
            disabled={isSaving}
            className="btn btn-ghost btn-sm text-error hover:bg-error/10"
          >
            Leave without saving
          </button>
          <button
            type="button"
            onClick={onStay}
            disabled={isSaving}
            className="btn btn-ghost btn-sm"
          >
            Stay on this page
          </button>
        </div>
      </div>
    </div>
  )
}
